// FILE: src/services/secondaryDimensionService.js
//
// Config-driven analytics + guidance + admin distribution for a cert's
// SECONDARY metadata dimension (declared via exams.js `secondaryDimension`,
// e.g. PMI-PMOCP "Focus Areas" on questions.focus_area / question field
// `focusArea`).
//
// Design rules (mirrors the PRIMARY-dimension/category code paths):
//   • EVERYTHING gates on getSecondaryDimension(examId). A cert with no
//     secondary dimension (pmp / cpmai / pmi-acp) returns an EMPTY result and
//     renders NO secondary-dimension UI. There are NO examId === 'pmi-pmocp'
//     conditionals anywhere.
//   • Learner performance is sourced from question_attempts joined to
//     questions — surfaced through learner_session_summary.secondary_breakdown
//     (migration 043), exactly parallel to phase_breakdown / category. See
//     performanceHistoryService.computeWeakPhases for the primary-dimension
//     analogue this mirrors.
//
// Pure functions are exported so unit tests can exercise them without Supabase.

import { getSecondaryDimension } from '../config/exams';
import { isKnownFocusArea } from '../config/examCategories';
import { loadLearnerSessions } from './performanceHistoryService';

// ── Study-guide guidance phrases ─────────────────────────────────────────────
//
// Maps each focus-area value to a short "prioritize ..." phrase used in the
// coaching guidance line. Keyed by the cert's secondaryDimension.key so a
// future second cert just adds its own block here (no per-cert conditionals in
// callers).
export const SECONDARY_GUIDANCE_PHRASES = {
  focusArea: {
    'PMO Strategy & Design':
      'PMO operating models, mandate definition, and structural design choices',
    'Governance & Oversight':
      'governance frameworks, decision rights, controls, and compliance oversight',
    'Portfolio Alignment':
      'portfolio prioritization, strategic alignment, and investment selection',
    'Benefits & Value Realization':
      'outcome tracking, benefits realization, KPI measurement, and strategic value',
    'PMO Services':
      'the service catalog, support models, and methodology/tooling enablement',
    'Resource & Capacity Management':
      'capacity planning, resource allocation, and demand-versus-supply balancing',
    'Reporting & Analytics':
      'dashboards, performance metrics, data quality, and executive reporting',
    'Organizational Change':
      'change adoption, communication planning, and resistance management',
    'PMO Maturity':
      'maturity assessment, continuous improvement, and capability roadmaps',
    'Stakeholder & Executive Engagement':
      'stakeholder mapping, executive sponsorship, and influence strategies',
  },
  // PMI-CPMAI ECO domains (September 2025 outline). Phrased against the ECO
  // tasks, not the methodology phases — the coach line must read as exam-domain
  // guidance so the learner never confuses it with lifecycle-phase feedback.
  ecoDomain: {
    'Support Responsible and Trustworthy AI Efforts':
      'bias and fairness, explainability, privacy and security governance, and accountability across the AI lifecycle',
    'Identify Business Needs and Solutions':
      'framing the business problem, AI feasibility and go/no-go decisions, success criteria, and stakeholder alignment',
    'Identify Data Needs':
      'data requirements, sourcing and sufficiency assessment, data-quality evaluation, and data access and compliance checks',
    'Manage AI Model Development and Evaluation':
      'data preparation and transformation, model selection, training, and evaluation against agreed metrics',
    'Operationalize AI Solution':
      'deployment planning, monitoring and drift, retraining triggers, and change management for AI in production',
  },
};

/**
 * Phrase for a single focus-area value under a given dimension key. Falls back
 * to a generic "concepts in this area" phrase for unknown values.
 */
export function guidancePhraseFor(dimensionKey, value) {
  const map = SECONDARY_GUIDANCE_PHRASES[dimensionKey] || {};
  return map[value] || 'the core concepts in this area';
}

// ── Pure aggregation ──────────────────────────────────────────────────────────

/**
 * Aggregates a learner's secondary-dimension accuracy across sessions and
 * returns strongest + weakest buckets.
 *
 * Mirrors performanceHistoryService.computeWeakPhases but:
 *   • reads s.secondaryBreakdown (NOT s.phaseBreakdown),
 *   • returns BOTH strongest and weakest (the dashboard panel shows both),
 *   • is gated by the dimension argument (null dimension → empty result).
 *
 * @param {object|null} dimension  - getSecondaryDimension(examId) result
 * @param {Array}       sessions   - normalized learner_session_summary rows
 * @param {object}      [opts]
 * @param {number}      [opts.minAttempts=5] - min answered to qualify a bucket
 * @param {number}      [opts.limit=3]       - max rows per list
 * @returns {{ areas: Array, strongest: Array, weakest: Array, hasData: boolean }}
 *   Each row: { area, accuracy, total, correct }
 */
export function computeSecondaryPerformance(dimension, sessions, opts = {}) {
  const empty = { areas: [], strongest: [], weakest: [], hasData: false };
  if (!dimension) return empty;
  if (!Array.isArray(sessions) || sessions.length === 0) return empty;

  const minAttempts = typeof opts.minAttempts === 'number' ? opts.minAttempts : 5;
  const limit       = typeof opts.limit === 'number' ? opts.limit : 3;

  const acc = {}; // area -> { total, correct }
  for (const s of sessions) {
    const sb = s && s.secondaryBreakdown ? s.secondaryBreakdown : null;
    if (!sb || typeof sb !== 'object') continue;
    for (const area of Object.keys(sb)) {
      const entry   = sb[area] || {};
      const total   = Number(entry.total)   || 0;
      const correct = Number(entry.correct) || 0;
      if (!acc[area]) acc[area] = { total: 0, correct: 0 };
      acc[area].total   += total;
      acc[area].correct += correct;
    }
  }

  const areas = Object.keys(acc).map((area) => {
    const { total, correct } = acc[area];
    const accuracy = total > 0 ? Math.round((100.0 * correct) / total) : 0;
    return { area, accuracy, total, correct };
  }).filter((r) => r.total >= minAttempts);

  if (areas.length === 0) return empty;

  const byAccAsc  = [...areas].sort((a, b) => a.accuracy - b.accuracy || b.total - a.total);
  const byAccDesc = [...areas].sort((a, b) => b.accuracy - a.accuracy || b.total - a.total);

  return {
    areas,
    weakest:   byAccAsc.slice(0, limit),
    strongest: byAccDesc.slice(0, limit),
    hasData:   true,
  };
}

/**
 * Builds a study-guide coaching line for the learner's WEAKEST secondary-area.
 * Falls back to null when the cert has no secondary dimension or there's no
 * qualifying data — callers then keep their existing domain-level guidance.
 *
 * @param {string} examId
 * @param {object} performance - result of computeSecondaryPerformance
 * @returns {{ label, area, accuracy, phrase, text } | null}
 */
export function buildSecondaryGuidance(examId, performance) {
  const dimension = getSecondaryDimension(examId);
  if (!dimension) return null;
  if (!performance || !performance.hasData || !performance.weakest?.length) return null;

  const worst  = performance.weakest[0];
  const phrase = guidancePhraseFor(dimension.key, worst.area);
  const label  = dimension.labelSingular || dimension.label || 'focus area';

  return {
    label,
    area:     worst.area,
    accuracy: worst.accuracy,
    phrase,
    text: `Your lowest ${label} is ${worst.area} (${worst.accuracy}% across ${worst.total} questions) — prioritize questions on ${phrase}.`,
  };
}

// ── Admin distribution aggregation ──────────────────────────────────────────

/**
 * Aggregates the question bank by the cert's secondary dimension for admin
 * visibility. Gated by getSecondaryDimension — returns null (no surface) for
 * certs without one.
 *
 * @param {object[]} questions - normalized question objects (camelCase; carry q.focusArea)
 * @param {string}   examId
 * @returns {null | {
 *   key, label, labelSingular,
 *   total, missing, invalid, unclassified,
 *   byArea: { [value]: number },      // declared vocabulary, zero-filled
 *   lowCount: string[],               // declared values with count < threshold
 *   lowCountThreshold: number,
 * }}
 */
export function buildSecondaryDistribution(questions, examId, opts = {}) {
  const dimension = getSecondaryDimension(examId);
  if (!dimension) return null;

  const threshold = typeof opts.lowCountThreshold === 'number' ? opts.lowCountThreshold : 5;
  const values    = Array.isArray(dimension.values) ? dimension.values : [];
  const unclassifiedLabel = dimension.unclassified || 'Unclassified';

  const byArea = Object.fromEntries(values.map((v) => [v, 0]));
  byArea[unclassifiedLabel] = 0;

  let missing = 0;
  let invalid = 0;
  let unclassified = 0;

  for (const q of (questions || [])) {
    const raw = (q && q[dimension.key] != null) ? String(q[dimension.key]).trim() : '';
    if (!raw) { missing++; continue; }
    if (raw === unclassifiedLabel) { byArea[unclassifiedLabel]++; unclassified++; continue; }
    if (values.includes(raw)) {
      byArea[raw]++;
    } else if (dimension.key === 'focusArea' && isKnownFocusArea(examId, raw)) {
      // Defensive (focus areas only): a value the config accepts but isn't in
      // the listed vocab. isKnownFocusArea is lenient for certs absent from
      // SECONDARY_DIMENSION_VALUES, so it must NOT be consulted for other
      // dimension keys — e.g. CPMAI's ecoDomain, where an off-vocabulary value
      // (say PMI-ACP's "Delivery") is exactly what `invalid` must count.
      byArea[raw] = (byArea[raw] || 0) + 1;
    } else {
      invalid++;
    }
  }

  const lowCount = values.filter((v) => byArea[v] < threshold);

  return {
    key:           dimension.key,
    label:         dimension.label || 'Secondary Dimension',
    labelSingular: dimension.labelSingular || dimension.label || 'Value',
    total:         (questions || []).length,
    missing,
    invalid,
    unclassified,
    byArea,
    lowCount,
    lowCountThreshold: threshold,
  };
}

// ── Loader (Supabase-backed) ─────────────────────────────────────────────────

/**
 * Loads the learner's secondary-dimension performance for an exam. Gated by
 * getSecondaryDimension — returns the empty result (no UI) for certs without a
 * secondary dimension WITHOUT touching Supabase.
 *
 * @param {string} examId
 * @param {object} [opts] - forwarded to computeSecondaryPerformance
 * @returns {Promise<ReturnType<typeof computeSecondaryPerformance>>}
 */
export async function loadSecondaryPerformance(examId, opts = {}) {
  const dimension = getSecondaryDimension(examId);
  const empty = { areas: [], strongest: [], weakest: [], hasData: false };
  if (!dimension) return empty;

  const sessions = await loadLearnerSessions();
  const forExam = (sessions || []).filter((s) => s.examId === examId);
  return computeSecondaryPerformance(dimension, forExam, opts);
}
